const steps = [
  {
    number: '01',
    icon: 'ri-user-add-line',
    title: 'Enter Your Details',
    description: 'Tell us about your pregnancy week or your baby\'s birth date. It takes less than 60 seconds to get started.',
    details: ['Pregnancy week or due date', 'Baby\'s birth date for post-care', 'Your location (Pune)'],
    color: 'teal'
  },
  {
    number: '02',
    icon: 'ri-file-list-3-line',
    title: 'Get Your Personalized Care Plan',
    description: 'Instantly receive a custom plan tailored to your stage from week-by-week guidance to hospital recommendations.',
    details: ['Weekly pregnancy milestones', 'Recommended hospital options', 'Upcoming checkup reminders'],
    color: 'sky'
  },
  {
    number: '03',
    icon: 'ri-heart-pulse-line',
    title: 'Track Health, Vaccines & Milestones',
    description: 'Follow your care journey with smart alerts for vaccines, checkups, and developmental milestones never miss a thing.',
    details: ['Vaccine schedule tracking', 'Health insight dashboard', 'Milestone celebrations'],
    color: 'rose'
  }
];

const colorStyles: Record<string, { badge: string; icon: string; dot: string; border: string }> = {
  teal: {
    badge: 'bg-teal-100 text-teal-700',
    icon: 'bg-teal-50 text-teal-600 border-teal-100',
    dot: 'bg-teal-500',
    border: 'border-teal-100 hover:border-teal-200'
  },
  sky: {
    badge: 'bg-sky-100 text-sky-700',
    icon: 'bg-sky-50 text-sky-600 border-sky-100',
    dot: 'bg-sky-500',
    border: 'border-sky-100 hover:border-sky-200'
  },
  rose: {
    badge: 'bg-rose-100 text-rose-700',
    icon: 'bg-rose-50 text-rose-600 border-rose-100',
    dot: 'bg-rose-500',
    border: 'border-rose-100 hover:border-rose-200'
  }
};

export default function HowItWorks() {
  return (
    <section id="how-it-works" className="py-20 sm:py-28 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-sky-50 border border-sky-100 rounded-full mb-5">
            <i className="ri-route-line text-sky-600"></i>
            <span className="text-sm font-semibold text-sky-700">Simple Process</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-5 leading-tight">
            How MomsFirstCare{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-sky-500">Works</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Three simple steps to get your complete pregnancy and baby care plan up and running today.
          </p>
        </div>

        <div className="relative">
          {/* Connector line - desktop only */}
          <div className="hidden lg:block absolute top-20 left-1/6 right-1/6 h-0.5 bg-gradient-to-r from-teal-200 via-sky-200 to-rose-200 z-0"></div>

          <div className="grid sm:grid-cols-3 gap-8 relative z-10">
            {steps.map((step, index) => {
              const styles = colorStyles[step.color];
              return (
                <div
                  key={index}
                  className={`bg-white rounded-2xl border ${styles.border} p-8 hover:-translate-y-2 transition-all`}
                >
                  <div className="flex items-center gap-3 mb-6">
                    <span className={`text-3xl font-black ${styles.badge.split(' ')[1]} opacity-30`}>
                      {step.number}
                    </span>
                    <div className={`w-12 h-12 flex items-center justify-center rounded-xl border ${styles.icon}`}>
                      <i className={`${step.icon} text-2xl`}></i>
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-5">{step.description}</p>
                  <ul className="space-y-2">
                    {step.details.map((detail, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-gray-500">
                        <span className={`w-1.5 h-1.5 rounded-full ${styles.dot} shrink-0`}></span>
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-12 text-center">
          <button
            onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
            className="px-8 py-4 bg-gradient-to-r from-teal-600 to-sky-500 text-white rounded-xl font-bold text-base hover:from-teal-700 hover:to-sky-600 transition-all hover:-translate-y-1 inline-flex items-center gap-2 whitespace-nowrap"
          >
            See All Features
            <i className="ri-arrow-down-line"></i>
          </button>
        </div>
      </div>
    </section>
  );
}
